import React, { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { Image as ImageIcon, Loader2, Download, Sparkles, Upload, X, Film, Play, Music, Ruler, DollarSign } from "lucide-react";
import { api, fileUrl } from "../lib/apiClient";
import { useFakeProgress } from "../lib/useFakeProgress";
import { toast } from "sonner";
import SpecSheetDialog from "./SpecSheetDialog";
import InteriorAssist from "./InteriorAssist";
import RoomCostDialog from "./RoomCostDialog";

const STYLES = ["Modern", "Contemporary", "Farmhouse", "Mediterranean", "Scandinavian", "Craftsman", "Mid-Century"];

export default function RenderModal({ open, onOpenChange, project, onVideoReady, tourClips = [], setTourClips, autoGenerate = false, shots = [], setShots }) {
  const [style, setStyle] = useState("Modern");
  const [mode, setMode] = useState("exterior");
  const [roomIdx, setRoomIdx] = useState(0);
  const [siding, setSiding] = useState("White render");
  const [accent, setAccent] = useState("Natural stone");
  const [roofColor, setRoofColor] = useState("Charcoal");
  const [loading, setLoading] = useState(false);
  const [renderPath, setRenderPath] = useState(null);
  const [lotFile, setLotFile] = useState(null);
  const [lotPreview, setLotPreview] = useState(null);
  const [roomFile, setRoomFile] = useState(null);
  const [roomPreview, setRoomPreview] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoPath, setVideoPath] = useState(null);
  const [videoElapsed, setVideoElapsed] = useState(0);
  const [videoDuration, setVideoDuration] = useState("6");
  const [stitching, setStitching] = useState(false);
  const [tourVideo, setTourVideo] = useState(null);
  const [composing, setComposing] = useState(false);
  const [composedVideo, setComposedVideo] = useState(null);
  const [showSpec, setShowSpec] = useState(false);
  const [showCost, setShowCost] = useState(false);
  const [costRid, setCostRid] = useState(null);
  const [assist, setAssist] = useState({});
  const [musicName, setMusicName] = useState(null);
  const [musicPath, setMusicPath] = useState(null);
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const musicRef = useRef(null);
  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const lotRef = useRef(null);
  const roomRef = useRef(null);
  const { progress, elapsed, est, start, done, reset } = useFakeProgress();
  const rooms = project.blueprint?.rooms || [];

  const pickLot = (f) => {
    if (!f) return;
    if (!/(jpeg|jpg|png|webp)/i.test(f.type)) { toast.error("Use JPEG, PNG or WEBP"); return; }
    setLotFile(f); setLotPreview(URL.createObjectURL(f));
  };
  const clearLot = () => { setLotFile(null); setLotPreview(null); };

  const pickRoom = (f) => {
    if (!f) return;
    if (!/(jpeg|jpg|png|webp)/i.test(f.type)) { toast.error("Use JPEG, PNG or WEBP"); return; }
    setRoomFile(f); setRoomPreview(URL.createObjectURL(f));
  };
  const clearRoom = () => { setRoomFile(null); setRoomPreview(null); };

  const generate = async (modeOverride) => {
    const m = modeOverride || mode;
    setLoading(true); setRenderPath(null); start(m === "interior" ? 60 : (lotFile ? 80 : 70));
    try {
      let res;
      const materials = `${siding} siding, ${accent} accents, ${roofColor} roof`;
      if (m === "interior") {
        const room = rooms[roomIdx] || { name: "Room", w: 12, h: 12 };
        if (roomFile) {
          const a = assist || {};
          const fd = new FormData();
          fd.append("file", roomFile);
          (a.samples || []).forEach((sf) => fd.append("samples", sf));
          const qs = new URLSearchParams({ room: a.roomDesc || room.name || "room", style, count: "3" });
          if (a.brief) qs.set("brief", a.brief);
          if (a.width) qs.set("width", a.width);
          if (a.length) qs.set("length", a.length);
          if (a.height) qs.set("height", a.height);
          const startRes = await api.post(`/ai/design-room?${qs.toString()}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          const taskId = startRes.data.task_id;
          const deadline = Date.now() + 200000;
          let doneData = null;
          while (Date.now() < deadline) {
            await new Promise((r) => setTimeout(r, 3000));
            const st = await api.get(`/ai/render-status/${taskId}`);
            if (st.data.status === "completed") { doneData = st.data; break; }
            if (st.data.status === "failed") throw new Error(st.data.error || "Design failed");
          }
          if (!doneData) throw new Error("Timed out");
          res = { data: { render_path: doneData.render_path } };
          if (doneData.redesign_id) setCostRid(doneData.redesign_id);
        } else {
          res = await api.post("/ai/render-room", { room_name: room.name, width: room.w, height: room.h, style });
        }
      } else if (lotFile) {
        const fd = new FormData();
        fd.append("file", lotFile);
        const qs = `name=${encodeURIComponent(project.name)}&style=${encodeURIComponent(style)}&roof=${encodeURIComponent(project.roofStyle || "")}&materials=${encodeURIComponent(materials)}&rooms=${encodeURIComponent(JSON.stringify(rooms))}`;
        res = await api.post(`/ai/render-context?${qs}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        const allFloors = (project.floors && project.floors.length) ? project.floors : [{ name: "Ground Floor", rooms }];
        res = await api.post("/ai/render-plan", { name: project.name, city: project.city, style, roof_style: project.roofStyle || "", materials, rooms, stories: allFloors.length, floors: allFloors, footprint_w: project.lot?.w ? Math.round(project.lot.w) : 0, footprint_d: project.lot?.h ? Math.round(project.lot.h) : 0, ref_path: project.sourcePhoto || null, terrain: project.terrain || "" });
      }
      setRenderPath(res.data.render_path);
      setVideoPath(null);
      done();
      toast.success(m === "interior" ? "Interior render ready" : (lotFile ? "Render placed on your lot" : "Exterior render ready"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Render failed");
      reset();
    } finally { setLoading(false); }
  };

  // One-click "Exterior render from blueprint": auto-run when opened via the Exterior button
  useEffect(() => {
    if (open && autoGenerate && !renderPath && !loading) {
      setMode("exterior");
      generate("exterior");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoGenerate]);

  const download = async () => {
    try {
      const res = await api.get(`/files/${renderPath}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${project.name || "render"}.png`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const stopVideo = () => {
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const animateVideo = async () => {
    if (!renderPath) return;
    stopVideo();
    setVideoLoading(true); setVideoPath(null); setVideoElapsed(0);
    timerRef.current = setInterval(() => setVideoElapsed((s) => s + 1), 1000);
    const room = rooms[roomIdx] || {};
    try {
      const res = await api.post("/ai/animate-render", {
        image_url: fileUrl(renderPath),
        mode,
        duration: videoDuration,
        style,
        room_name: mode === "interior" ? (room.name || "room") : "",
      });
      const taskId = res.data.task_id;
      const poll = async () => {
        try {
          const st = await api.get(`/ai/animate-status/${taskId}`);
          if (st.data.status === "completed") {
            stopVideo();
            setVideoPath(st.data.video_path);
            setVideoLoading(false);
            onVideoReady && onVideoReady(st.data.video_path);
            if (setTourClips) {
              const label = mode === "interior" ? (room.name || "Room") : "Exterior";
              setTourClips((c) => [...c, { path: st.data.video_path, label }]);
            }
            toast.success("Cinematic walkabout ready");
          } else if (st.data.status === "failed") {
            stopVideo();
            setVideoLoading(false);
            toast.error(st.data.error || "Video generation failed");
          } else {
            pollRef.current = setTimeout(poll, 4000);
          }
        } catch {
          pollRef.current = setTimeout(poll, 5000);
        }
      };
      pollRef.current = setTimeout(poll, 4000);
    } catch (e) {
      stopVideo();
      setVideoLoading(false);
      toast.error(e.response?.data?.detail || "Could not start video generation");
    }
  };

  const downloadVideo = async () => {
    try {
      const res = await api.get(`/files/${videoPath}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${project.name || "walkabout"}.mp4`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const pickMusic = async (f) => {
    if (!f) return;
    if (!/(mpeg|mp3|wav|aac|ogg|m4a|audio)/i.test(f.type) && !/\.(mp3|wav|m4a|aac|ogg)$/i.test(f.name)) {
      toast.error("Use an MP3, WAV, M4A, AAC or OGG file"); return;
    }
    setUploadingMusic(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/ai/upload-music", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMusicPath(res.data.music_path);
      setMusicName(f.name);
      toast.success("Music added — it'll play under your tour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Music upload failed");
    } finally { setUploadingMusic(false); }
  };
  const clearMusic = () => { setMusicPath(null); setMusicName(null); };

  const stitchTour = async () => {
    if (tourClips.length < 2) return;
    setStitching(true); setTourVideo(null);    try {
      const res = await api.post("/ai/stitch-tour", { video_paths: tourClips.map((c) => c.path), music_path: musicPath });
      setTourVideo(res.data.video_path);
      onVideoReady && onVideoReady(res.data.video_path);
      toast.success("Room-to-room tour stitched");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Stitch failed");
    } finally { setStitching(false); }
  };

  const addShot = () => {
    if (!renderPath || !setShots) return;
    const label = mode === "interior" ? (rooms[roomIdx]?.name || "Room") : "Exterior";
    if (shots.some((s) => s.path === renderPath)) { toast.info("This render is already in the video"); return; }
    setShots((s) => [...s, { path: renderPath, label }]);
    toast.success(`Added "${label}" to the video`);
  };

  const composeVideo = async () => {
    if (!shots.length) return;
    setComposing(true); setComposedVideo(null);
    try {
      const start = await api.post("/ai/compose-video", { image_paths: shots.map((s) => s.path), music_path: musicPath, seconds_per_image: 3.5 });
      const taskId = start.data.task_id;
      const deadline = Date.now() + 180000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2500));
        const st = await api.get(`/ai/compose-status/${taskId}`);
        if (st.data.status === "completed") { setComposedVideo(st.data.video_path); onVideoReady && onVideoReady(st.data.video_path); toast.success("Walkthrough video composed"); break; }
        if (st.data.status === "failed") { toast.error(st.data.error || "Compose failed"); break; }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not compose the video");
    } finally { setComposing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { reset(); setRenderPath(null); clearLot(); clearRoom(); setCostRid(null); stopVideo(); setVideoLoading(false); setVideoPath(null); setTourVideo(null); setStitching(false); setComposedVideo(null); setComposing(false); } }}>
      <DialogContent className="max-w-2xl bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-xl">
            <ImageIcon className="w-5 h-5 text-[#38BDF8]" /> AI 3D Render
          </DialogTitle>
          <DialogDescription className="text-[#8B949E]">Generate a photoreal exterior or interior render with Gemini, then animate it into a short cinematic video with fal.ai.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2 p-1 rounded-lg bg-[#0D1117] border border-[#30363D] w-fit">
            {["exterior", "interior"].map((m) => (
              <button key={m} onClick={() => { setMode(m); setRenderPath(null); }} data-testid={`render-mode-${m}`}
                className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${mode === m ? "bg-[#38BDF8] text-[#0D1117]" : "text-[#8B949E] hover:text-[#F0F6FC]"}`}>
                {m}
              </button>
            ))}
          </div>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="text-xs text-[#8B949E]">Architectural Style</Label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger data-testid="render-style-select" className="bg-[#0D1117] border-[#30363D]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
                  {STYLES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="render-generate-btn" onClick={generate} disabled={loading} className="bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Rendering…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate</>}
            </Button>
          </div>

          {mode === "interior" && (
            <div>
              <Label className="text-xs text-[#8B949E]">Room to render</Label>
              <Select value={String(roomIdx)} onValueChange={(v) => setRoomIdx(Number(v))}>
                <SelectTrigger data-testid="render-room-select" className="bg-[#0D1117] border-[#30363D]"><SelectValue placeholder="Pick a room" /></SelectTrigger>
                <SelectContent className="bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
                  {rooms.map((r, i) => <SelectItem key={r.id || i} value={String(i)}>{r.name} ({r.w}×{r.h}ft)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {mode === "interior" && (
          <div
            onClick={() => !roomPreview && roomRef.current?.click()}
            className="rounded-lg border border-dashed border-[#30363D] bg-[#0D1117] p-3 flex items-center gap-3 cursor-pointer hover:border-[#34D399] transition-colors"
            data-testid="room-dropzone">
            <input ref={roomRef} type="file" accept="image/*" className="hidden" data-testid="room-photo-input" onChange={(e) => pickRoom(e.target.files[0])} />
            {roomPreview ? (
              <>
                <img src={roomPreview} alt="room" className="w-16 h-12 object-cover rounded-md border border-[#30363D]" />
                <div className="flex-1 text-xs text-[#34D399]">Room photo added — the render will be based on your picture.</div>
                <button onClick={(e) => { e.stopPropagation(); clearRoom(); }} className="text-[#8B949E] hover:text-[#EF4444]" data-testid="room-clear-btn"><X className="w-4 h-4" /></button>
              </>
            ) : (
              <>
                <Upload className="w-5 h-5 text-[#8B949E]" />
                <div className="flex-1 text-xs text-[#8B949E]">Optional: upload a photo of your <span className="text-[#F0F6FC]">actual room</span> to redesign & furnish it to scale.</div>
              </>
            )}
          </div>
          )}

          {mode === "interior" && (
            <InteriorAssist roomFile={roomFile} style={style} accent="#38BDF8" onChange={setAssist} />
          )}

          {mode === "exterior" && (
          <div
            onClick={() => !lotPreview && lotRef.current?.click()}
            className="rounded-lg border border-dashed border-[#30363D] bg-[#0D1117] p-3 flex items-center gap-3 cursor-pointer hover:border-[#34D399] transition-colors"
            data-testid="lot-dropzone">
            <input ref={lotRef} type="file" accept="image/*" className="hidden" data-testid="lot-photo-input" onChange={(e) => pickLot(e.target.files[0])} />
            {lotPreview ? (
              <>
                <img src={lotPreview} alt="lot" className="w-16 h-12 object-cover rounded-md border border-[#30363D]" />
                <div className="flex-1 text-xs text-[#34D399]">Lot photo added — house will be rendered onto your land.</div>
                <button onClick={(e) => { e.stopPropagation(); clearLot(); }} className="text-[#8B949E] hover:text-[#EF4444]" data-testid="lot-clear-btn"><X className="w-4 h-4" /></button>
              </>
            ) : (
              <>
                <Upload className="w-5 h-5 text-[#8B949E]" />
                <div className="flex-1 text-xs text-[#8B949E]">Optional: upload a photo of your lot/land to render the house <span className="text-[#F0F6FC]">in context</span>.</div>
              </>
            )}
          </div>
          )}

          {mode === "exterior" && (
            <div className="grid grid-cols-3 gap-2" data-testid="material-palette">
              {[["Siding", siding, setSiding, ["White render", "Timber cladding", "Dark fibre-cement", "Red brick", "Beige stucco"]],
                ["Accent", accent, setAccent, ["Natural stone", "Slate", "Brick", "Cedar wood", "Concrete"]],
                ["Roof", roofColor, setRoofColor, ["Charcoal", "Terracotta", "Black metal", "Green", "Weathered zinc"]]].map(([lbl, val, setter, opts]) => (
                <div key={lbl}>
                  <Label className="text-xs text-[#8B949E]">{lbl}</Label>
                  <Select value={val} onValueChange={setter}>
                    <SelectTrigger data-testid={`material-${lbl.toLowerCase()}-select`} className="bg-[#0D1117] border-[#30363D] h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
                      {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}

          {loading && (
            <div className="space-y-2">
              <Progress value={progress} className="h-2 bg-[#0D1117]" />
              <div className="flex justify-between text-xs text-[#8B949E] font-mono-cad">
                <span>Rendering your house… {Math.round(progress)}%</span>
                <span>{elapsed.toFixed(0)}s / ~{est}s</span>
              </div>
            </div>
          )}

          {(project.gallery || []).length > 0 && (
            <div data-testid="render-gallery">
              <Label className="text-xs text-[#8B949E]">Saved renders</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {project.gallery.map((g, i) => (
                  <button key={i} onClick={() => { setRenderPath(g.path); setVideoPath(null); }}
                    data-testid={`gallery-shot-${i}`}
                    className="group relative rounded-lg overflow-hidden border border-[#30363D] hover:border-[#38BDF8] transition-colors">
                    <img src={fileUrl(g.path)} alt={g.label} className="w-full h-16 object-cover" />
                    <span className="absolute bottom-0 inset-x-0 bg-black/60 text-[10px] text-[#F0F6FC] px-1.5 py-0.5 truncate font-mono-cad">{g.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-[#30363D] bg-[#090D12] min-h-[280px] flex items-center justify-center overflow-hidden" data-testid="render-result">
            {renderPath ? (
              <img src={fileUrl(renderPath)} alt="AI render" className="w-full h-full object-cover animate-fade-up" />
            ) : (
              <p className="text-sm text-[#6E7681] px-6 text-center">Your photoreal render will appear here.</p>
            )}
          </div>

          {renderPath && (
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={download} variant="outline" className="border-[#30363D] hover:border-[#38BDF8] hover:text-[#38BDF8]" data-testid="render-download-btn">
                <Download className="w-4 h-4 mr-1.5" /> Download
              </Button>
              <Button onClick={addShot} variant="outline" className="border-[#30363D] hover:border-[#34D399] hover:text-[#34D399]" data-testid="add-shot-btn">
                <Film className="w-4 h-4 mr-1.5" /> Add to video
              </Button>
              <Button onClick={() => setShowSpec(true)} variant="outline" className="col-span-2 border-[#34D399]/50 text-[#34D399] hover:border-[#34D399] hover:text-[#34D399]" data-testid="render-spec-btn">
                <Ruler className="w-4 h-4 mr-1.5" /> Measured Spec (PDF)
              </Button>
              {mode === "interior" && costRid && (
                <Button onClick={() => setShowCost(true)} variant="outline" className="col-span-2 border-[#34D399]/40 text-[#34D399] hover:border-[#34D399] hover:text-[#34D399]" data-testid="render-cost-btn">
                  <DollarSign className="w-4 h-4 mr-1.5" /> Materials & Cost
                </Button>
              )}
            </div>
          )}

          {shots.length > 0 && (
            <div className="rounded-xl border border-[#34D399]/30 bg-[#0D1117] p-4 space-y-3" data-testid="compose-section">
              <div className="flex items-center gap-2">
                <Film className="w-4 h-4 text-[#34D399]" />
                <span className="font-heading font-semibold text-sm">Compose Walkthrough Video</span>
              </div>
              <p className="text-xs text-[#8B949E]">Stitches your saved renders into a smooth pan-and-zoom walkthrough video with music. Works instantly — no AI wait.</p>
              <div className="flex flex-wrap gap-1.5" data-testid="compose-shots">
                {shots.map((s, i) => (
                  <span key={i} className="text-[11px] px-2 py-1 rounded-md bg-[#161B22] border border-[#30363D] text-[#8B949E] font-mono-cad flex items-center gap-1">
                    {i + 1}. {s.label}
                    <button onClick={() => setShots((arr) => arr.filter((_, j) => j !== i))} className="text-[#6E7681] hover:text-[#EF4444]" data-testid={`compose-shot-remove-${i}`}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
              <div
                onClick={() => !musicName && musicRef.current?.click()}
                className="rounded-lg border border-dashed border-[#30363D] bg-[#161B22] p-2.5 flex items-center gap-2.5 cursor-pointer hover:border-[#34D399] transition-colors"
                data-testid="compose-music-dropzone">
                <input ref={musicRef} type="file" accept="audio/*" className="hidden" data-testid="music-input" onChange={(e) => pickMusic(e.target.files[0])} />
                {uploadingMusic ? <Loader2 className="w-4 h-4 animate-spin text-[#34D399]" /> : <Music className="w-4 h-4 text-[#34D399]" />}
                {musicName ? (
                  <>
                    <div className="flex-1 text-xs text-[#34D399] truncate">{musicName}</div>
                    <button onClick={(e) => { e.stopPropagation(); clearMusic(); }} className="text-[#8B949E] hover:text-[#EF4444]" data-testid="music-clear-btn"><X className="w-4 h-4" /></button>
                  </>
                ) : (
                  <div className="flex-1 text-xs text-[#8B949E]">Optional: add <span className="text-[#F0F6FC]">background music</span>.</div>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={composeVideo} disabled={composing} data-testid="compose-video-btn" className="flex-1 bg-[#34D399] hover:bg-[#10B981] text-[#0D1117] font-semibold">
                  {composing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Composing…</> : <><Film className="w-4 h-4 mr-2" /> Compose {shots.length}-shot video</>}
                </Button>
                <Button onClick={() => setShots([])} variant="outline" disabled={composing} data-testid="compose-clear-btn" className="border-[#30363D] text-[#8B949E] hover:text-[#EF4444]">Clear</Button>
              </div>
              {composedVideo && (
                <div className="space-y-2" data-testid="compose-result">
                  <video src={fileUrl(composedVideo)} controls autoPlay loop playsInline className="w-full rounded-lg border border-[#30363D] bg-black" />
                  <Button onClick={async () => { try { const r = await api.get(`/files/${composedVideo}`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = "walkthrough.mp4"; a.click(); URL.revokeObjectURL(u); } catch { toast.error("Download failed"); } }} variant="outline" className="w-full border-[#30363D] hover:border-[#34D399] hover:text-[#34D399]" data-testid="compose-download-btn">
                    <Download className="w-4 h-4 mr-1.5" /> Download Walkthrough Video
                  </Button>
                </div>
              )}
            </div>
          )}

          {renderPath && (
            <div className="rounded-xl border border-[#A855F7]/30 bg-[#0D1117] p-4 space-y-3" data-testid="video-section">
              <div className="flex items-center gap-2">
                <Film className="w-4 h-4 text-[#A855F7]" />
                <span className="font-heading font-semibold text-sm">Animate to Cinematic Video</span>
              </div>
              <p className="text-xs text-[#8B949E]">
                Turn this {mode === "interior" ? "interior" : "exterior"} render into a short cinematic {mode === "interior" ? "walkthrough" : "drone reveal"} clip with AI.
              </p>
              <div className="flex items-end gap-3">
                <div className="w-32">
                  <Label className="text-xs text-[#8B949E]">Length</Label>
                  <Select value={videoDuration} onValueChange={setVideoDuration}>
                    <SelectTrigger data-testid="video-duration-select" className="bg-[#161B22] border-[#30363D] h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
                      <SelectItem value="6">6 seconds</SelectItem>
                      <SelectItem value="10">10 seconds</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={animateVideo} disabled={videoLoading} data-testid="video-generate-btn" className="bg-[#A855F7] hover:bg-[#9333EA] text-white font-semibold">
                  {videoLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Play className="w-4 h-4 mr-2" /> Generate Video</>}
                </Button>
              </div>

              {videoLoading && (
                <div className="flex items-center gap-2 text-xs text-[#8B949E] font-mono-cad">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-[#A855F7]" />
                  <span>Rendering cinematic video… this can take 1–3 min · {videoElapsed}s</span>
                </div>
              )}

              {videoPath && (
                <div className="space-y-2" data-testid="video-result">
                  <video src={fileUrl(videoPath)} controls autoPlay loop muted playsInline
                    className="w-full rounded-lg border border-[#30363D] bg-black" />
                  <Button onClick={downloadVideo} variant="outline" className="w-full border-[#30363D] hover:border-[#A855F7] hover:text-[#A855F7]" data-testid="video-download-btn">
                    <Download className="w-4 h-4 mr-1.5" /> Download Video
                  </Button>
                </div>
              )}
            </div>
          )}
          {tourClips.length > 0 && (
            <div className="rounded-xl border border-[#38BDF8]/30 bg-[#0D1117] p-4 space-y-3" data-testid="tour-section">
              <div className="flex items-center gap-2">
                <Film className="w-4 h-4 text-[#38BDF8]" />
                <span className="font-heading font-semibold text-sm">Room-to-Room Walkthrough Tour</span>
              </div>
              <p className="text-xs text-[#8B949E]">Every clip you generate is collected here. Stitch them into one continuous walkthrough video.</p>
              <div className="flex flex-wrap gap-1.5" data-testid="tour-clips">
                {tourClips.map((c, i) => (
                  <span key={i} className="text-[11px] px-2 py-1 rounded-md bg-[#161B22] border border-[#30363D] text-[#8B949E] font-mono-cad">{i + 1}. {c.label}</span>
                ))}
              </div>
              <div
                onClick={() => !musicName && musicRef.current?.click()}
                className="rounded-lg border border-dashed border-[#30363D] bg-[#161B22] p-2.5 flex items-center gap-2.5 cursor-pointer hover:border-[#38BDF8] transition-colors"
                data-testid="music-dropzone">
                <input ref={musicRef} type="file" accept="audio/*" className="hidden" data-testid="music-input" onChange={(e) => pickMusic(e.target.files[0])} />
                {uploadingMusic ? <Loader2 className="w-4 h-4 animate-spin text-[#38BDF8]" /> : <Music className="w-4 h-4 text-[#38BDF8]" />}
                {musicName ? (
                  <>
                    <div className="flex-1 text-xs text-[#38BDF8] truncate">{musicName}</div>
                    <button onClick={(e) => { e.stopPropagation(); clearMusic(); }} className="text-[#8B949E] hover:text-[#EF4444]" data-testid="music-clear-btn"><X className="w-4 h-4" /></button>
                  </>
                ) : (
                  <div className="flex-1 text-xs text-[#8B949E]">Optional: add <span className="text-[#F0F6FC]">background music</span> to play under the tour.</div>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={stitchTour} disabled={stitching || tourClips.length < 2} data-testid="stitch-tour-btn" className="flex-1 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold">
                  {stitching ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Stitching…</> : <><Film className="w-4 h-4 mr-2" /> Stitch {tourClips.length} clips into tour</>}
                </Button>
                <Button onClick={() => setTourClips && setTourClips([])} variant="outline" disabled={stitching} data-testid="tour-clear-btn" className="border-[#30363D] text-[#8B949E] hover:text-[#EF4444]">Clear</Button>
              </div>
              {tourClips.length < 2 && <p className="text-[11px] text-[#6E7681]">Generate at least 2 clips (e.g. different rooms) to stitch a tour.</p>}
              {tourVideo && (
                <div className="space-y-2" data-testid="tour-result">
                  <video src={fileUrl(tourVideo)} controls autoPlay loop muted playsInline className="w-full rounded-lg border border-[#30363D] bg-black" />
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
      <SpecSheetDialog
        open={showSpec}
        onOpenChange={setShowSpec}
        imagePath={renderPath}
        kind={mode}
        title={`${project.name || "Design"} \u2014 ${mode === "interior" ? (rooms[roomIdx]?.name || "Interior") : "Exterior"}`}
        subtitle={mode === "interior" ? "Interior render" : `${style} exterior`}
        known={mode === "interior" ? { width: rooms[roomIdx]?.w || 0, length: rooms[roomIdx]?.h || 0, height: 0 } : {}}
      />
      <RoomCostDialog open={showCost} onOpenChange={setShowCost} redesignId={costRid} selectedIndex={0} />
    </Dialog>
  );
}
